// --------------------------
// IMPORTS
// --------------------------
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import useCurrentPath, {
  decryptData,
  downloadMonthWiseExpenseRequestReport,

} from "../../utils/Constants";

import TopContainerHeader from "../../components/molecules/TopComponentHeader";
import {
  getExpenseRequestsListThunk,
  searchExpenseDataThunk,
} from "../../store/thunks/listOfExpensesThunk";
import { getPendingRequestDetailThunk } from "../../store/thunks/pendingRequestsThunk";
import { useSearchAndPagination } from "../../utils/useSearchAndPagination";
import { useLocation } from "react-router-dom";

import TableContent from "../../components/atoms/TableContent";
import { Tooltip, message } from "antd";
import {
  getCurrencyListDetailThunk,
  getDepartmentListThunk,
  getExpenseCodeListThunk,
  getLocationListThunk,
  getProjectCodeListThunk,
  getUserListWithDetailThunk,
} from "../../store/thunks/masterDataThunk";
import media from "../../media";
import html2pdf from "html2pdf.js";
import ExportPdf from "../../utils/exportPdf";
import ReactDOMServer from "react-dom/server";
import PendingRequestDetail from "./pendingRequestDetail";

// --------------------------
// MAIN COMPONENT
// --------------------------
export default function PendingRequests() {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const currentPath = useCurrentPath();
  const location = useLocation();
  const autoSearchDone = useRef(false);
  const isDownloadingRef = useRef(false);
  const openedViaDirectIdRef = useRef(false);

  // --------------------------
  // REDUX STATE
  // --------------------------
  const storeRole = useSelector((state) => state.auth.role);
  const storeLIUId = useSelector((state) => state.auth.lIUId);
  const storeToken = useSelector((state) => state.auth.accessToken);
  let storeUserInfo = useSelector((state) => state.auth.userInfo);
  storeUserInfo = decryptData(storeUserInfo);

  const storeEmpID = storeUserInfo?.user?.empID;
  const storeAccessTokenExpiresOn = useSelector(
    (state) => state.auth.accessTokenExpiresOn,
  );

  const pendingRequests =
    useSelector((state) => state.pendingRequests.list) || [];

  const storeList = useSelector(
    (state) => state.auth.list?.[currentPath + "_info"] || [],
  );

  const ercount = useSelector((state) => {
    return state.auth.ercount;
  });

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [showLoader, setShowLoader] = useState(true);
  const [filteredPendingRequests, setFilteredPendingRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);
  const [expenseCodes, setExpenseCodes] = useState([]);
  const [allCurrencies, setAllCurrencies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [searchFilterData, setSearchFilterData] = useState("");
  const [storeTotalCount, setStoreTotalCount] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchCount, setSearchCount] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [isFilteredDates, setIsFilteredDates] = useState(false);
  const [filterValues, setFilterValues] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [startRange, setStartRange] = useState("");
  const [endRange, setEndRange] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [selectedRecordKey, setSelectedRecordKey] = useState(null);
  const [actionViewSelected, setActionViewSelected] = useState(null);
  const [refreshFlag, setRefreshFlag] = useState(false);

  // --------------------------
  // LOCAL VARIABLES
  // --------------------------
  const directDetailPageRenderedID = location.pathname?.split("/")?.[2];

  // Memoize filteredList
  const filteredList = useMemo(() => {
    // Ensure decrypted is an array before calling map
    if (Array.isArray(storeList)) {
      return storeList.map(({ key, id, ...rest }) => rest);
    }

    // Return an empty array if decryption fails or result is not an array
    return [];
  }, [storeList]);

  // --------------------------
  // HANDLERS
  // --------------------------
  const handleRefreshList = () => {
    setRefreshFlag((prev) => !prev); // toggle to trigger useEffect
  };

  const handleView = (key, viewTitle) => {
    setSelectedRecordKey(key);
    setIsVisible(true);
    setActionViewSelected(viewTitle);
  };

  const generatePdfContent = (
    record,
    expenseDetail,
    storeRole,
    storeToken,
    storeAccessTokenExpiresOn,
  ) => {
    return ReactDOMServer.renderToString(
      <ExportPdf
        record={record}
        expenseDetail={expenseDetail}
        storeRole={storeRole}
        storeToken={storeToken}
        storeAccessTokenExpiresOn={storeAccessTokenExpiresOn}
        users={users}
        locations={locations}
        departments={departments}
        expenseCodes={expenseCodes}
        projectCodes={projectCodes}
        allCurrencies={allCurrencies}
      />,
    );
  };

  const handlePdfExport = async (record) => {
    try {
      let pdfRecord = record;

      if (
        record?.natureOfExpense === "Travel Settlement" &&
        Array.isArray(record?.travelSettlement) &&
        record.travelSettlement.length > 0 &&
        Array.isArray(locations) &&
        locations.length > 0
      ) {
        const locationId = record.travelSettlement[0].location;

        const matchedLocation = locations.find(
          (loc) => String(loc.value) === String(locationId),
        );

        if (matchedLocation) {
          pdfRecord = {
            ...record,
            travelSettlement: record.travelSettlement.map((ts, idx) =>
              idx === 0
                ? {
                    ...ts,
                    location: matchedLocation?.label,
                  }
                : ts,
            ),
          };
        }
      }

      // Fetch expense details
      
      const payload = {id:record?.key};
      const response = await dispatch(getPendingRequestDetailThunk(payload)).unwrap();      
      
      if (response) {
        // Step 1: Generate HTML content as a string
        const htmlContent = generatePdfContent(
          pdfRecord,
          response,
          storeRole,
          storeToken,
          storeAccessTokenExpiresOn,
        );

        // Define PDF options
        const options = {
          margin: [10, 10, 10, 10],
          filename: `${record.key}_Details.pdf`,
          html2canvas: {
            scale: 2, // Increase scale for better resolution
            useCORS: true,
            logging: true,
          },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "portrait",
          },
        };

        // Generate PDF
        html2pdf()
          .from(htmlContent)
          .set(options)
          .save()
          .catch((error) => {
            console.error("Error generating PDF:", error);
          });
      }
    } catch (error) {
      console.error("Error fetching data or generating PDF:", error);
    }
  };

  const handleMonthRangeChange = (range) => {
    setSearchFilterData("");
    setShowLoader(true);
    if (range && range.length === 2) {
      const startMonth = range[0]?.format("YYYY-MM");
      const endMonth = range[1]?.format("YYYY-MM");

      setFilterValues({ startMonth, endMonth });
      setIsFilteredDates(true);
    } else {
      setFilterValues(null);
      setIsFilteredDates(false);
    }
  };

  const handleDownloadMWERReport = async () => {
    try {
      isDownloadingRef.current = true;
      setShowLoader(true);

      const hasSearchValue = document.querySelector(
        ".dashboardSearch input",
      ).value;

      await downloadMonthWiseExpenseRequestReport({
        storeRole,
        storeToken,
        storeLIUId,
        storeAccessTokenExpiresOn,
        currentPath,
        status: [
          process.env.REACT_APP_PENDING_STATUS?.toUpperCase(),
          process.env.REACT_APP_NORESPONSE_STATUS.replace(
            " ",
            "",
          )?.toUpperCase(),
          process.env.REACT_APP_RESUBMITTED_STATUS?.toUpperCase(),
          process.env.REACT_APP_SEEK_ACK_STATUS.replace(" ", "")?.toUpperCase(),
          process.env.REACT_APP_ACK_REC_STATUS.replace(" ", "")?.toUpperCase(),
        ],
        hasSearchValue,
      });
    } catch (err) {
      message.error("Download failed");
    } finally {
      isDownloadingRef.current = false;
      setShowLoader(false);
    }
  };

  const { handleGlSearch, handlePaginationChange, pagination } =
    useSearchAndPagination({
      searchThunk: searchExpenseDataThunk,
      listThunk: getExpenseRequestsListThunk,
      currentPath,
      role: storeRole,
      lIUId: storeLIUId,
      setFilteredData: setFilteredData,
      setStoreTotalCount,
      status: [
        process.env.REACT_APP_PENDING_STATUS?.toUpperCase(),
        process.env.REACT_APP_NORESPONSE_STATUS.replace(" ", "").toUpperCase(),

        process.env.REACT_APP_RESUBMITTED_STATUS?.toUpperCase(),
        process.env.REACT_APP_SEEK_ACK_STATUS.replace(" ", "")?.toUpperCase(),
        process.env.REACT_APP_ACK_REC_STATUS.replace(" ", "")?.toUpperCase(),
      ],
      month: startRange && endRange ? { startRange, endRange } : "",
      onSearchStatusChange: (searching, count) => {
        setIsSearching(searching);
        //setShowLoader(searching);
        setSearchCount(count);
      },
    });

  const onPageChange = async (page, pageSize, currentSearchText = "") => {
    const trimmedSearch = currentSearchText && currentSearchText; //?.trim()
    setShowLoader(true);

    try {
      if (trimmedSearch) {
        await handleGlSearch({
          value: trimmedSearch,
          page,
          pageSize,
        });
      } else {
        await handlePaginationChange(page, pageSize);
      }
    } finally {
      if (!isDownloadingRef.current) {
        setShowLoader(false);
      }
    }
  };

  // --------------------------
  // API CALLS
  // --------------------------
  const getExpenseRequestsList = async (source = "", pagination = "") => {
    setSearchFilterData?.("");
    setStoreTotalCount("");

    const inputElement = document.querySelector(".dashboardSearch span input");
    const hasSearchValue = inputElement?.value || "";

    const allStates = {
      showLoader,
      setShowLoader,
      pagination: pagination || { current: 1, page: 1, pageSize: 50 },
      status: [
        process.env.REACT_APP_PENDING_STATUS?.toUpperCase(),
        process.env.REACT_APP_NORESPONSE_STATUS.replace(" ", "").toUpperCase(),

        process.env.REACT_APP_RESUBMITTED_STATUS?.toUpperCase(),
        process.env.REACT_APP_SEEK_ACK_STATUS.replace(" ", "")?.toUpperCase(),
        process.env.REACT_APP_ACK_REC_STATUS.replace(" ", "")?.toUpperCase(),
      ],
      filterValues: { startRange, endRange },
      searchKeyword: hasSearchValue || "",
    };

    try {
      await dispatch(
        getExpenseRequestsListThunk({
          route: location.pathname.replace("/", ""),
          allStates,
        }),
      )
        .unwrap()
        .then((response) => {
          if (response?.data) {
            setFilteredData(response?.data);
            setStoreTotalCount(response?.totalCount);
          }
        });
    } catch (error) {
      console.error("Error fetching expense requests list: ", error);
    } finally {
      if (!isDownloadingRef.current) {
        setShowLoader(false);
      }
    }
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------
  // Use useEffect to update filteredData when filteredList changes
  useEffect(() => {
    if (!isFilteredDates && filteredData !== filteredList) {
      //setFilteredData(filteredList);
    }
  }, [filteredList, filteredData, isFilteredDates]);

  useEffect(() => {
    if (filterValues) {
      setStartRange(filterValues.startMonth);
      setEndRange(filterValues.endMonth);
    } else {
      setStartRange("");
      setEndRange("");
    }
  }, [filterValues]);

  useEffect(() => {
    // If no filter is active, always load (for list view)

    //setShowLoader(false);
    if (isDownloadingRef.current) return;
    if (!directDetailPageRenderedID) {
      getExpenseRequestsList();
    }

    // // If filter is active, load filtered data once ranges have values
    // if (isFilterSelected && startRange && endRange) {
    //   setShowLoader(true);
    //   getExpenseRequestsList("filtered", pagination);
    // }
  }, [
    currentPath,
    startRange,
    endRange,
    directDetailPageRenderedID,
    refreshFlag,
  ]);

  useEffect(() => {
    if (!directDetailPageRenderedID) return;
    setSearchText(directDetailPageRenderedID);
    const checkPopup = () =>
      !!document.querySelector(".addExpensePopupFormWraper-visible");

    // Initial check
    if (!checkPopup()) {
      setShowLoader(false);
    }

    if (!autoSearchDone.current) {
      autoSearchDone.current = true;
      (async () => {
        const res = await handleGlSearch({
          source: "directDetailPageRenderedID",
          value: directDetailPageRenderedID,
        });

        const matchedRecord = res?.data?.find(
          (item) => String(item.key) === String(directDetailPageRenderedID),
        );

        if (matchedRecord) {
          openedViaDirectIdRef.current = true;
          handleView(matchedRecord.key, "View"); //  AFTER search
        }
      })();
    }

    // Watch DOM for popup appearing/disappearing
    const observer = new MutationObserver(() => {
      const popupVisible = checkPopup();

      // Hide loader if popup becomes visible
      if (popupVisible) {
        setShowLoader(false);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [directDetailPageRenderedID, handleGlSearch]);

  useEffect(() => {
    setShowLoader(true);
    dispatch(getUserListWithDetailThunk())
      .unwrap()
      .then((res) => {
        setUsers(res || []);
      });
    dispatch(getDepartmentListThunk())
      .unwrap()
      .then((res) => {
        setDepartments(res || []);
      });
    dispatch(getProjectCodeListThunk())
      .unwrap()
      .then((res) => {
        setProjectCodes(res || []);
      });
    dispatch(getExpenseCodeListThunk())
      .unwrap()
      .then((res) => {
        setExpenseCodes(res || []);
      });
    dispatch(getCurrencyListDetailThunk())
      .unwrap()
      .then((res) => {
        setAllCurrencies(res || []);
      });
    dispatch(getLocationListThunk())
      .unwrap()
      .then((res) => {
        setLocations(res || []);
      });
  }, [dispatch, currentPath, storeRole, storeLIUId]);

  // --------------------------
  // TABLE COLUMNS
  // --------------------------
  const columns = [
    {
      title: "Sr. No.",
      render: (text, record, index) => index + 1,
      className: "all-nowrap-column",
    },
    {
      title: "Name",
      dataIndex: "empName",
      key: "empName",
      className: "all-nowrap-column",
      render: (text, record, index) => {
        return (
          <>
            <div>{text}</div>
            {record?.empID && <small>Emp. Id: {record.empID}</small>}
          </>
        );
      },
    },
    {
      title: "Nature of Expense",
      dataIndex: "natureOfExpense",
      className: "all-nowrap-column",
    },
    {
      title: "Vendor Name",
      dataIndex: "businessPurpose",
      className: "all-nowrap-column",
      render: (text, record, index) => {
        const maxLength = 15;
        const truncatedText =
          text && text.length > maxLength
            ? text.substring(0, maxLength) + "..."
            : text;
        return <span title={text}>{truncatedText}</span>;
      },
    },
    {
      title: "Project Code",
      dataIndex: "projectCodeID",
      className: "all-nowrap-column",
      render: (text, record, index) => {
        const projectCodeInfo = projectCodes.find(
          (projectCodeItem) => projectCodeItem.value == text,
        );

        if (typeof projectCodeInfo?.label !== "string") return null;
        const [code = "", companyName = ""] =
          (projectCodeInfo?.label && projectCodeInfo?.label?.split(" - ")) ||
          [];

        return (
          <>
            <div>{code}</div>
            <small>{companyName}</small>
          </>
        );
      },
    },
    {
      title: "Expense Period",
      dataIndex: "expensePeriod",
      className: "all-nowrap-column",
      render: (text) => {
        if (typeof text !== "string") return null;
        const [fromText = "", toText = ""] = (text && text?.split(" - ")) || [];

        const [fromDayMonth = "", fromYear = ""] =
          (fromText && fromText?.split(", ")) || [];
        const [toDayMonth = "", toYear = ""] =
          (toText && toText?.split(", ")) || [];

        return (
          <div className="splitDates">
            <span>
              {fromDayMonth}
              <small>{fromYear}</small>
            </span>
            <span>-</span>
            <span>
              {toDayMonth}
              <small>{toYear}</small>
            </span>
          </div>
        );
      },
    },
    {
      title: "Expense Month",
      dataIndex: "month",
      className: "all-nowrap-column",
    },
    {
      title: "Department Code",
      dataIndex: "departmentID",
      className: "all-nowrap-column",
      render: (text) => {
        let deptId = "";
        let deptName = "";
        let deptCode = "";

        if (text && typeof text === "object") {
          /** CASE 1: departmentID is an object */
          deptId = text._id || "";
          deptName = typeof text.name === "string" ? text.name : "";
          deptCode = typeof text.code === "string" ? text.code : "";
        } else {
          /** CASE 2: departmentID is a string */
          deptId = text;
          const deptInfo = departments.find((d) => d.value == text);

          if (deptInfo?.label) {
            const [name = "", code = ""] = deptInfo.label.split(" - ");
            deptName = name;
            deptCode = code;
          }
        }

        return (
          <>
            <div>{deptName}</div>
            <small>{deptCode || ""}</small>
          </>
        );
      },
    },
    {
      title: "Expense Code",
      dataIndex: "expenseCodeID",
      className: "all-nowrap-column",
      render: (text) => {
        const expCodeInfo = expenseCodes.find(
          (expCodeItem) => expCodeItem.value == text,
        );

        if (typeof expCodeInfo?.label !== "string") return null;
        const [code = "", name = ""] =
          (expCodeInfo?.label && expCodeInfo?.label?.split(" - ")) || [];

        return (
          <>
            <div>{code}</div>
            <small>{name}</small>
          </>
        );
      },
    },
    {
      title: "Currency",
      dataIndex: "currencyID",
      className: "all-nowrap-column",
      render: (text, record) => {
        const currencyInfo = allCurrencies.find(
          (currItem) => currItem.id === text,
        );

        if (typeof currencyInfo?.code !== "string") return null;

        return currencyInfo?.code;
      },
    },
    {
      title: "Travel Settlement Amount",
      dataIndex: "travelSettlement",
      className: "all-nowrap-column",
      render: (text, record) => {
        const locationCurrencyInfo = locations.find(
          (currItem) => currItem.id === record?.travelSettlement?.[0]?.location,
        );
        const total = record?.travelSettlement?.[0]?.totalINRAmount;
        return total ? (
          <span className="table-btn cursor-unset text-capitalize amount-btn">
            {locationCurrencyInfo?.symbol_native &&
              locationCurrencyInfo?.symbol_native}
            <img src={media.documentNormal.svg} alt="Rupee" />
            <span> {total}</span>
          </span>
        ) : (
          <span className="table-btn cursor-unset text-capitalize amount-btn">
            <span>-</span>
          </span>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "cost",
      className: "all-nowrap-column",
      render: (text, record) => {
        const currencyInfo = allCurrencies.find(
          (currItem) => currItem.id === record?.currencyID,
        );
        return (
          text && (
            <span className="table-btn cursor-unset text-capitalize amount-btn">
              <span>
                {currencyInfo?.symbol_native && currencyInfo?.symbol_native}
                {text}
              </span>
            </span>
          )
        );
      },
    },
    {
      title: "Total Amount",
      dataIndex: "totalReimbursement",
      className: "all-nowrap-column",
      render: (text) =>
        text && (
          <span className={`table-btn cursor-unset text-capitalize amount-btn`}>
            <img src={media.documentNormal.svg} alt="Rupee" />
            <span>{text}</span>
          </span>
        ),
    },
    {
      title: "Approvers",
      dataIndex: "approvers",
      render: (approvers) => {
        if (!approvers || !approvers.length) return null;

        // Keep only latest entry per userID
        const uniqueApprovers = Object.values(
          approvers.reduce((acc, curr) => {
            const prev = acc[curr.userID];

            if (!prev || curr.dateTime > prev.dateTime) {
              acc[curr.userID] = curr;
            }

            return acc;
          }, {})
        );
        return (
          <div className="d-flex justify-content-start">
            {uniqueApprovers &&
              uniqueApprovers.map((approver, index) => {
                const approverInfo = users.find(
                  (userItem) => userItem.id === approver?.userID,
                );

                return (
                  <Tooltip
                    key={index}
                    title={`${approverInfo?.empName.toUpperCase()}, ${
                      approver?.status ? approver?.status : "NOT SENT"
                    }`}
                    className={`approvers-status-tooltip mr-5`}
                  >
                    <span>
                      {approverInfo?.profilePicUrl ? (
                        <>
                          <img
                            src={
                              process.env.REACT_APP_BACKEND +
                              "/images/" +
                              approverInfo?.profilePicUrl
                            }
                            alt="card icon"
                          />
                        </>
                      ) : (
                        <span className="initialLetter">
                          {approverInfo?.empName
                            ? approverInfo?.empName.charAt(0).toUpperCase()
                            : "?"}
                        </span>
                      )}
                      <b
                        className={`approvers-tooltips ${
                          approver?.status
                            ? approver?.status?.toLowerCase()
                            : "yetToSent"
                        }-tooltips`}
                      ></b>
                    </span>
                  </Tooltip>
                );
              })}
          </div>
        );
      },
    },
    {
      title: "Admin Action",
      dataIndex: "adminApprover",
      className: "all-nowrap-column",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      className: "all-nowrap-column",
      render: (text) => (
        <span
          className={`table-btn cursor-unset text-capitalize ${text?.toLowerCase()}-btn`}
        >
          {text?.toLowerCase() ===
          process.env.REACT_APP_SEEK_ACK_STATUS.replace(" ", "")?.toLowerCase()
            ? process.env.REACT_APP_SEEK_ACK_STATUS
            : text?.toLowerCase() ===
                process.env.REACT_APP_ACK_REC_STATUS.replace(
                  " ",
                  "",
                )?.toLowerCase()
              ? process.env.REACT_APP_ACK_REC_STATUS
              : text?.toLowerCase() ===
                  process.env.REACT_APP_NORESPONSE_STATUS.replace(
                    " ",
                    "",
                  )?.toLowerCase()
                ? process.env.REACT_APP_NORESPONSE_STATUS
                : text?.toLowerCase()}
        </span>
      ),
    },
    {
      title: "Payment Paid On",
      dataIndex: "paymentPaidOn",
      className: "all-nowrap-column",
      key: "paymentPaidOn",
      render: (date, record) => {
        if (!moment(date).isValid()) return "-";

        return (
          <>
            {moment(date).format("Do MMM, YYYY")}
            {record?.modeOfPayment && (
              <>
                <small>
                  <strong>(Mode of Payment: </strong> {record?.modeOfPayment})
                </small>
              </>
            )}
          </>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text, record) => {
        const draft =
          record.status === process.env.REACT_APP_DRAFT_STATUS.toUpperCase() &&
          record.empID === storeEmpID
            ? 1
            : 0;
        const link =
          draft === 1
            ? `/${process.env.REACT_APP_DASHBOARD_EL_SEARCH_IN}/draft/${record?.key}`
            : `/${process.env.REACT_APP_DASHBOARD_EL_SEARCH_IN}/${record?.key}`;
        const viewTitle = draft === 1 ? "View Draft" : "View";

        return (
          <>
            <div className="table-menu-container">
              <div className="table-menu-icon text-grey-custom">⋯</div>
              <div className="table-dropdown-menu">
                <div className="table-menu-item">
                  <div
                    className="text-grey-custom text-size-12 viwLink cursor-pointer"
                    title={`${viewTitle}`}
                    onClick={() => handleView(record?.key, viewTitle)}
                  >
                    <i className="flaticon-eye icon-lg text-grey-custom"></i>{" "}
                    {`${viewTitle}`}
                  </div>
                </div>

                <div
                  className="table-menu-item"
                  onClick={() => handlePdfExport(record)}
                >
                  <i
                    className="fas fa-file-pdf icon-lg text-grey-custom cursor-pointer"
                    title="Download PDF"
                  ></i>{" "}
                  <span className="text-grey-custom">Download PDF</span>
                </div>
                {draft === 1 && (
                  <div className="table-menu-item">
                    <div
                      title="Publish"
                      // onClick={() => {
                      //   handleDraftExpenseForPublish(record?.key);
                      // }}
                    >
                      <i
                        className="fas fa-paper-plane icon-lg text-grey-custom cursor-pointer"
                        aria-hidden="true"
                      ></i>{" "}
                      <span className="text-grey-custom">Publish</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        );
      },
    },
  ];

  const detailPageRenderSource =
    directDetailPageRenderedID &&
    currentPath?.split("/")?.[0] ===
      process.env.REACT_APP_DASHBOARD_PR_SEARCH_IN
      ? false
      : true;

  useEffect(() => {
    if (!isVisible && openedViaDirectIdRef.current) {
      // Reset ONLY for direct-ID flow
      openedViaDirectIdRef.current = false;
      setSearchText("");
      //getExpenseRequestsList();
    }
  }, [isVisible]);
  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <>
      <div className="container">
        <TopContainerHeader
          sectionNameClass="col-lg-7 text-left commonTitleText"
          sectionTitle="Pending Requests"
          sectionDescription={
            showLoader
              ? "Loading..."
              : isSearching
                ? `${storeTotalCount} Found`
                : `${filteredData?.length} Found`
          }
          hasSectionButton={false}
          onFilteredData={setFilteredPendingRequests}
          hasFilter={true}
          listingStatus={false}
          handleMonthRangeChange={handleMonthRangeChange}
          handleDownloadMWERReport={handleDownloadMWERReport}
          isGlSearch={true}
          onSearchValue={(value) => {
            setShowLoader(true);

            handleGlSearch({ value }).finally(() => setShowLoader(false));
          }}
          searchText={searchText}
          setSearchText={setSearchText}
          hasSearch={true}
          hasSearchButton="general"
          searchClassName="table-search dashboardSearch"
          searchPlaceholder="Search Name, Nature of Expense, EmpID, Vendor Name & Total Amount"
          searchData={pendingRequests}
          searchKeys={[
            "empName",
            "natureOfExpense",
            "empID",
            "vendorName",
            "totalAmount",
          ]}
        />

        <div
          className={`card p-5 rounded-lg paddingTop-0 ${
            (
              isSearching
                ? storeTotalCount <=
                  process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE
                : ercount <= process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE
            )
              ? "paddingBottom-0"
              : "minHeight-100vh"
          }`}
        >
          <div className="table-area">
            <div className="table-responsive">
              <div className="tab-content" id="alltable-size">
                <TableContent
                  storeList={filteredData}
                  columns={columns}
                  bordered={false}
                  className="expenseMultitasking"
                  rowClassName="editable-row"
                  loading={showLoader}
                  pagination={pagination}
                  totalCount={storeTotalCount}
                  //handlePaginationChange={onPageChange}
                  handlePaginationChange={handlePaginationChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {isVisible &&
        (actionViewSelected === "View" ||
          (directDetailPageRenderedID &&
            currentPath?.split("/")?.[0] ===
              process.env.REACT_APP_DASHBOARD_PR_SEARCH_IN)) && (
          <PendingRequestDetail
            detailPageRenderSource={detailPageRenderSource}
            isVisible={isVisible}
            setIsVisible={setIsVisible}
            recordKey={selectedRecordKey}
            onCloseReset={handleRefreshList}
          />
        )}
    </>
  );
}
